import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { loginTranslations } from "./i18n/loginTranslations";
import { forgotPasswordTranslations } from "./i18n/forgotPasswordTranslations";
import { resetPasswordTranslations } from "./i18n/resetPasswordTranslations";
import { authCallbackTranslations } from "./i18n/authCallbackTranslations";
import { signupTranslations } from "./i18n/signupTranslations";
import { onboardingTranslations, industriesTranslations, bankSyncTranslations } from "./i18n/onboardingTranslations";
import { emailVerificationTranslations } from "./i18n/emailVerificationTranslations";
import { emailVerifiedTranslations } from "./i18n/emailVerifiedTranslations";
import { twoFactorTranslations } from "./i18n/twoFactorTranslations";
import { assistantTranslations } from "./i18n/assistantTranslations";
import { analyticsTranslations } from "./i18n/analyticsTranslations";
import { cashflowTranslations } from "./i18n/cashflowTranslations";
import { transactionsTranslations } from "./i18n/transactionsTranslations";
import { budgetsTranslations } from "./i18n/budgetsTranslations";
import { calendarTranslations } from "./i18n/calendarTranslations";
import { clientsTranslations } from "./i18n/clientsTranslations";
import { projectsTranslations } from "./i18n/projectsTranslations";
import { vendorsTranslations } from "./i18n/vendorsTranslations";
import { categoriesTranslations } from "./i18n/categoriesTranslations";
import { teamMembersTranslations } from "./i18n/teamMembersTranslations";
import { bankConnectionsTranslations } from "./i18n/bankConnectionsTranslations";
import { billingTranslations } from "./i18n/billingTranslations";
import { peopleTranslations } from "./i18n/peopleTranslations";
import { settingsTranslations } from "./i18n/settingsTranslations";
import { shellTranslations } from "./i18n/shellTranslations";
import { notificationsTranslations } from "./i18n/notificationsTranslations";
import { commandPaletteTranslations } from "./i18n/commandPaletteTranslations";
import { profileTranslations } from "./i18n/profileTranslations";
import { pulseCheckTranslations } from "./i18n/pulseCheckTranslations";

type TranslationSections = {
  login: unknown;
  forgot_password: unknown;
  reset_password: unknown;
  auth_callback: unknown;
  signup: unknown;
  onboarding: unknown;
  industries: unknown;
  bank_sync: unknown;
  bank_connections: unknown;
  billing: unknown;
  settings: unknown;
  email_verification: unknown;
  email_verified: unknown;
  two_factor: unknown;
  assistant: unknown;
  analytics: unknown;
  cashflow: unknown;
  transactions: unknown;
  budgets: unknown;
  calendar: unknown;
  clients: unknown;
  projects: unknown;
  vendors: unknown;
  people: unknown;
  categories: unknown;
  team_members: unknown;
  profile: unknown;
  notifications: unknown;
  command_palette: unknown;
  navigation: unknown;
  topbar: unknown;
  tenant_selector: unknown;
  help: unknown;
  feedback: unknown;
  sidebar: unknown;
  trial: unknown;
  profit_pulse: unknown;
  pulse_check: unknown;
  modes: unknown;
};

const resources = Object.keys(loginTranslations).reduce<Record<string, { translation: TranslationSections }>>(
  (acc, lang) => {
    const langKey = lang as keyof typeof loginTranslations;
    acc[lang] = {
      translation: {
        login: loginTranslations[langKey],
        forgot_password: forgotPasswordTranslations[langKey] ?? forgotPasswordTranslations.en,
        reset_password: resetPasswordTranslations[langKey] ?? resetPasswordTranslations.en,
        auth_callback: authCallbackTranslations[langKey] ?? authCallbackTranslations.en,
        signup: signupTranslations[langKey] ?? signupTranslations.en,
        onboarding: onboardingTranslations[langKey] ?? onboardingTranslations.en,
        industries: industriesTranslations[langKey] ?? industriesTranslations.en,
        bank_sync: bankSyncTranslations[langKey] ?? bankSyncTranslations.en,
        bank_connections: bankConnectionsTranslations[langKey] ?? bankConnectionsTranslations.en,
        billing: billingTranslations[langKey] ?? billingTranslations.en,
        settings: settingsTranslations[langKey] ?? settingsTranslations.en,
        email_verification: emailVerificationTranslations[langKey] ?? emailVerificationTranslations.en,
        email_verified: emailVerifiedTranslations[langKey] ?? emailVerifiedTranslations.en,
        two_factor: twoFactorTranslations[langKey] ?? twoFactorTranslations.en,
        assistant: assistantTranslations[langKey] ?? assistantTranslations.en,
        analytics: analyticsTranslations[langKey] ?? analyticsTranslations.en,
        cashflow: cashflowTranslations[langKey] ?? cashflowTranslations.en,
        transactions: transactionsTranslations[langKey] ?? transactionsTranslations.en,
        budgets: budgetsTranslations[langKey] ?? budgetsTranslations.en,
        calendar: calendarTranslations[langKey] ?? calendarTranslations.en,
        clients: clientsTranslations[langKey] ?? clientsTranslations.en,
        projects: projectsTranslations[langKey] ?? projectsTranslations.en,
        vendors: vendorsTranslations[langKey] ?? vendorsTranslations.en,
        people: peopleTranslations[langKey] ?? peopleTranslations.en,
        categories: categoriesTranslations[langKey] ?? categoriesTranslations.en,
        team_members: teamMembersTranslations[langKey] ?? teamMembersTranslations.en,
        profile: profileTranslations[langKey] ?? profileTranslations.en,
        notifications: notificationsTranslations[langKey] ?? notificationsTranslations.en,
        command_palette: commandPaletteTranslations[langKey] ?? commandPaletteTranslations.en,
        navigation: shellTranslations[langKey]?.navigation ?? shellTranslations.en.navigation,
        topbar: shellTranslations[langKey]?.topbar ?? shellTranslations.en.topbar,
        tenant_selector: shellTranslations[langKey]?.tenant_selector ?? shellTranslations.en.tenant_selector,
        help: shellTranslations[langKey]?.help ?? shellTranslations.en.help,
        feedback: shellTranslations[langKey]?.feedback ?? shellTranslations.en.feedback,
        sidebar: shellTranslations[langKey]?.sidebar ?? shellTranslations.en.sidebar,
        trial: shellTranslations[langKey]?.trial ?? shellTranslations.en.trial,
        profit_pulse: shellTranslations[langKey]?.profit_pulse ?? shellTranslations.en.profit_pulse,
        modes: shellTranslations[langKey]?.modes ?? shellTranslations.en.modes,
        pulse_check: pulseCheckTranslations[langKey] ?? pulseCheckTranslations.en,
      },
    };
    return acc;
  },
  {},
);

const supportedLanguages = ["pt", "en", "es", "et", "de", "fr", "it", "nl", "sv", "da", "fi"];

const getInitialLanguage = () => {
  try {
    const stored = localStorage.getItem("moneylights-language");
    if (stored && supportedLanguages.includes(stored)) {
      return stored;
    }
  } catch {
    // ignore storage errors
  }

  try {
    const browserLang = navigator.language.split("-")[0];
    if (supportedLanguages.includes(browserLang)) {
      return browserLang;
    }
  } catch {
    // ignore navigator errors
  }

  return "en";
};

i18n.use(initReactI18next).init({
  resources,
  lng: getInitialLanguage(),
  fallbackLng: "en",
  interpolation: { escapeValue: false },
  react: { useSuspense: false },
});

i18n.on("languageChanged", (lng) => {
  try {
    localStorage.setItem("moneylights-language", lng);
    document.documentElement.lang = lng;
  } catch {
    // ignore storage errors
  }
});

export default i18n;
